import * as THREE from "three";
import { CAR_PRESETS, computeStats, type CarUpgrade, type InputState, type VehicleBlueprint } from "@neon-arena/shared";
import { SceneManager } from "../game/SceneManager.js";
import { VehicleController } from "../physics/VehicleController.js";
import { buildVehicleVisual, type VehicleVisual } from "../rendering/VehicleRenderer.js";
import { PerformanceTracker } from "./PerformanceTracker.js";

export type CompArena = "drag" | "skidpan" | "weapons";

const ARENA_SIZE = 600;
const NO_OBSTACLES: never[] = [];

const COLOR_A = 0x22d3ee;  // cyan
const COLOR_B = 0xf472b6;  // pink

interface CompVehicle {
  blueprint: VehicleBlueprint;
  upgrades: CarUpgrade;
  controller: VehicleController;
  visual: VehicleVisual;
  label: THREE.Sprite;
  labelCtx: CanvasRenderingContext2D;
  labelTex: THREE.CanvasTexture;
  colorStr: string;
  spawnX: number;
  spawnZ: number;
  spawnRotY: number;
  tracker: PerformanceTracker | null; // drag only
}

export class ComparisonLab {
  private vehicles: CompVehicle[] = [];
  private animFrame = 0;
  private lastFrame = 0;
  private onExit: () => void;
  private keyHandler: (e: KeyboardEvent) => void;
  private inputReader: () => InputState;
  private overlayEl: HTMLDivElement;

  constructor(
    private scene: SceneManager,
    private bpA: VehicleBlueprint,
    private upgradesA: CarUpgrade,
    private bpB: VehicleBlueprint,
    private upgradesB: CarUpgrade,
    private arena: CompArena,
    inputReader: () => InputState,
    overlayEl: HTMLDivElement,
    onExit: () => void,
  ) {
    this.onExit = onExit;
    this.inputReader = inputReader;
    this.overlayEl = overlayEl;
    this.keyHandler = (e) => {
      if (e.code === "Escape") this.stop();
      if (e.code === "KeyR" && this.arena === "drag") this.resetDrag();
    };
  }

  start(): void {
    while (this.scene.scene.children.length > 0) {
      this.scene.scene.remove(this.scene.scene.children[0]);
    }
    this.scene.scene.fog = null;
    this.buildArena();
    this.spawnVehicles();
    this.buildOverlay();
    window.addEventListener("keydown", this.keyHandler);
    this.lastFrame = performance.now();
    this.loop();
  }

  stop(): void {
    cancelAnimationFrame(this.animFrame);
    window.removeEventListener("keydown", this.keyHandler);
    while (this.scene.scene.children.length > 0) {
      this.scene.scene.remove(this.scene.scene.children[0]);
    }
    this.vehicles = [];
    this.overlayEl.innerHTML = "";
    this.onExit();
  }

  private resetDrag(): void {
    for (const v of this.vehicles) {
      v.controller.teleport(v.spawnX, 0, v.spawnZ, v.spawnRotY);
      v.tracker?.reset();
    }
  }

  private loop = (): void => {
    const now = performance.now();
    let dt = (now - this.lastFrame) / 1000;
    if (dt > 0.1) dt = 0.1;
    this.lastFrame = now;

    const input = this.inputReader();

    for (const v of this.vehicles) {
      const vehicleInput = this.arena === "weapons" && v === this.vehicles[1]
        ? this.mirrorSteer(input)
        : input;
      v.controller.applyInput(vehicleInput, dt);
      const snap = v.controller.snapshot();
      v.visual.group.position.set(snap.x, 0, snap.z);
      v.visual.group.rotation.y = snap.rotY;
      v.label.position.set(snap.x, 4.5, snap.z);
      this.drawLabel(v, `${Math.abs(snap.speed).toFixed(1)} m/s`);

      if (this.arena === "drag" && v.tracker) {
        v.tracker.tick(snap.speed, vehicleInput.throttle, dt);
      }
    }

    this.updateOverlay();
    this.updateCamera();
    this.scene.render();
    this.animFrame = requestAnimationFrame(this.loop);
  };

  private mirrorSteer(input: InputState): InputState {
    return { ...input, steer: -input.steer };
  }

  private spawnVehicles(): void {
    const spawns = this.getSpawnConfigs();
    const pairs: [VehicleBlueprint, CarUpgrade, number, number, number, string][] = [
      [this.bpA, this.upgradesA, spawns[0].x, spawns[0].z, spawns[0].rotY, `#${COLOR_A.toString(16).padStart(6, "0")}`],
      [this.bpB, this.upgradesB, spawns[1].x, spawns[1].z, spawns[1].rotY, `#${COLOR_B.toString(16).padStart(6, "0")}`],
    ];

    for (const [bp, upgrades, sx, sz, rotY, colorStr] of pairs) {
      const controller = new VehicleController(NO_OBSTACLES, bp, { x: sx, y: 0, z: sz, rotY }, { arenaSize: ARENA_SIZE });
      controller.applyUpgrades(upgrades);
      const visual = buildVehicleVisual(bp);
      this.scene.scene.add(visual.group);

      const cv = document.createElement("canvas");
      cv.width = 256; cv.height = 80;
      const ctx = cv.getContext("2d")!;
      const tex = new THREE.CanvasTexture(cv);
      const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
      const label = new THREE.Sprite(mat);
      label.scale.set(10, 3, 1);
      this.scene.scene.add(label);

      const tracker = this.arena === "drag"
        ? new PerformanceTracker(computeStats(bp, upgrades).topSpeed)
        : null;

      const vehicle: CompVehicle = {
        blueprint: bp, upgrades, controller, visual,
        label, labelCtx: ctx, labelTex: tex, colorStr,
        spawnX: sx, spawnZ: sz, spawnRotY: rotY,
        tracker,
      };
      this.drawLabel(vehicle, "— m/s");
      this.vehicles.push(vehicle);
    }
  }

  private getSpawnConfigs(): Array<{ x: number; z: number; rotY: number }> {
    switch (this.arena) {
      case "drag":
        return [
          { x: -5, z: -150, rotY: 0 },
          { x:  5, z: -150, rotY: 0 },
        ];
      case "skidpan":
        return [
          { x: -8, z:  0, rotY: 0 },
          { x:  8, z:  0, rotY: 0 },
        ];
      case "weapons":
        return [
          { x: 0, z: -40, rotY: 0 },
          { x: 0, z:  40, rotY: Math.PI },
        ];
    }
  }

  private drawLabel(v: CompVehicle, speed: string): void {
    const ctx = v.labelCtx;
    ctx.clearRect(0, 0, 256, 80);
    ctx.fillStyle = "rgba(4,6,13,0.82)";
    ctx.fillRect(0, 0, 256, 80);
    ctx.font = "bold 15px monospace";
    ctx.fillStyle = v.colorStr;
    ctx.textAlign = "center";
    const pName = findPresetName(v.upgrades);
    const nameLabel = pName !== "stock" ? `${v.blueprint.name} [${pName}]` : v.blueprint.name;
    ctx.fillText(nameLabel, 128, 22);
    ctx.font = "bold 24px monospace";
    ctx.fillStyle = "#fb923c";
    ctx.fillText(speed, 128, 56);
    v.labelTex.needsUpdate = true;
  }

  private buildOverlay(): void {
    const a = this.vehicles[0];
    const b = this.vehicles[1];
    const arenaLabel = { drag: "DRAG STRIP", skidpan: "SKIDPAN", weapons: "WEAPONS RANGE" }[this.arena];
    const nameA = `${esc(a.blueprint.name)}${findPresetName(a.upgrades) !== "stock" ? ` <small>[${findPresetName(a.upgrades)}]</small>` : ""}`;
    const nameB = `${esc(b.blueprint.name)}${findPresetName(b.upgrades) !== "stock" ? ` <small>[${findPresetName(b.upgrades)}]</small>` : ""}`;

    const statsA = computeStats(a.blueprint, a.upgrades);
    const statsB = computeStats(b.blueprint, b.upgrades);

    const dragMetricsHtml = this.arena === "drag" ? `
      <div class="co-section-label">LIVE</div>
      <div class="co-row"><span id="co-a-speed" class="co-val co-a-val">—</span><span class="co-stat-label">SPEED</span><span id="co-b-speed" class="co-val co-b-val">—</span></div>

      <div class="co-section-label">METRICS</div>
      <div class="co-row co-row-sub"><span class="co-sub co-a-sub">${statsA.topSpeed.toFixed(1)}</span><span class="co-stat-label">REF TOP</span><span class="co-sub co-b-sub">${statsB.topSpeed.toFixed(1)}</span></div>
      <div class="co-row"><span id="co-a-t50" class="co-val co-a-val">—</span><span class="co-stat-label">T-50%</span><span id="co-b-t50" class="co-val co-b-val">—</span></div>
      <div class="co-row"><span id="co-a-t80" class="co-val co-a-val">—</span><span class="co-stat-label">T-80%</span><span id="co-b-t80" class="co-val co-b-val">—</span></div>
      <div class="co-row"><span id="co-a-peak" class="co-val co-a-val">—</span><span class="co-stat-label">PEAK SPD</span><span id="co-b-peak" class="co-val co-b-val">—</span></div>
      <div class="co-row"><span id="co-a-accel" class="co-val co-a-val">—</span><span class="co-stat-label">ACCEL T</span><span id="co-b-accel" class="co-val co-b-val">—</span></div>
      <div class="co-row"><span id="co-a-dist" class="co-val co-a-val">—</span><span class="co-stat-label">DISTANCE</span><span id="co-b-dist" class="co-val co-b-val">—</span></div>

      <div class="co-section-label">Δ A − B</div>
      <div class="co-delta-row"><span class="co-delta-label">Δ T-50%</span><span id="co-d-t50" class="co-delta-val">—</span></div>
      <div class="co-delta-row"><span class="co-delta-label">Δ T-80%</span><span id="co-d-t80" class="co-delta-val">—</span></div>
      <div class="co-delta-row"><span class="co-delta-label">Δ PEAK SPD</span><span id="co-d-peak" class="co-delta-val">—</span></div>

      <div id="co-tracker-state" class="co-tracker-state">WAITING FOR THROTTLE</div>
    ` : `
      <div class="co-row"><span id="co-a-speed" class="co-val co-a-val">—</span><span class="co-stat-label">SPEED</span><span id="co-b-speed" class="co-val co-b-val">—</span></div>
      <div class="co-row"><span id="co-a-head" class="co-val co-a-val">—</span><span class="co-stat-label">HEADING</span><span id="co-b-head" class="co-val co-b-val">—</span></div>
      <div class="co-row"><span id="co-a-pos" class="co-val co-a-val">—</span><span class="co-stat-label">POSITION</span><span id="co-b-pos" class="co-val co-b-val">—</span></div>
    `;

    const hint = this.arena === "drag"
      ? "WASD drive (mirrored) &nbsp;|&nbsp; R reset &nbsp;|&nbsp; ESC exit"
      : "WASD drive (mirrored) &nbsp;|&nbsp; ESC exit";

    this.overlayEl.innerHTML = `
      <div class="co-arena-label">${arenaLabel}</div>
      <div class="co-header">
        <span class="co-a-title">${nameA}</span>
        <span class="co-vs">VS</span>
        <span class="co-b-title">${nameB}</span>
      </div>
      <div id="co-stats">${dragMetricsHtml}</div>
      <div class="co-hint">${hint}</div>
    `;
  }

  private updateOverlay(): void {
    if (this.vehicles.length < 2) return;
    const snapA = this.vehicles[0].controller.snapshot();
    const snapB = this.vehicles[1].controller.snapshot();

    const set = (id: string, val: string): void => {
      const el = document.getElementById(id);
      if (el) el.textContent = val;
    };

    set("co-a-speed", `${Math.abs(snapA.speed).toFixed(1)}`);
    set("co-b-speed", `${Math.abs(snapB.speed).toFixed(1)}`);

    if (this.arena === "drag") {
      const mA = this.vehicles[0].tracker!.getMetrics();
      const mB = this.vehicles[1].tracker!.getMetrics();

      const fmt = (v: number | null, unit = "s"): string =>
        v !== null ? `${v.toFixed(2)}${unit}` : "—";
      const fmtSpd = (v: number): string => v > 0 ? v.toFixed(1) : "—";
      const fmtDelta = (a: number | null, b: number | null, unit = "s", invert = false): string => {
        if (a === null || b === null) return "—";
        const d = invert ? b - a : a - b;
        return `${d >= 0 ? "+" : ""}${d.toFixed(2)}${unit}`;
      };
      const deltaColor = (el: HTMLElement | null, a: number | null, b: number | null, lowerIsBetter: boolean): void => {
        if (!el || a === null || b === null) return;
        const better = lowerIsBetter ? a < b : a > b;
        el.className = "co-delta-val " + (a === b ? "" : better ? "co-delta-better" : "co-delta-worse");
      };

      set("co-a-t50",   fmt(mA.timeTo50));
      set("co-b-t50",   fmt(mB.timeTo50));
      set("co-a-t80",   fmt(mA.timeTo80));
      set("co-b-t80",   fmt(mB.timeTo80));
      set("co-a-peak",  fmtSpd(mA.maxSpeedReached));
      set("co-b-peak",  fmtSpd(mB.maxSpeedReached));
      set("co-a-accel", fmt(mA.accelerationTime));
      set("co-b-accel", fmt(mB.accelerationTime));
      set("co-a-dist",  mA.distanceTravelled > 0 ? `${mA.distanceTravelled.toFixed(0)}m` : "—");
      set("co-b-dist",  mB.distanceTravelled > 0 ? `${mB.distanceTravelled.toFixed(0)}m` : "—");

      set("co-d-t50",  fmtDelta(mA.timeTo50,  mB.timeTo50));
      set("co-d-t80",  fmtDelta(mA.timeTo80,  mB.timeTo80));
      set("co-d-peak", fmtDelta(mA.maxSpeedReached, mB.maxSpeedReached, "", false));

      const dT50 = document.getElementById("co-d-t50");
      const dT80 = document.getElementById("co-d-t80");
      const dPeak = document.getElementById("co-d-peak");
      deltaColor(dT50,  mA.timeTo50,  mB.timeTo50,  true);
      deltaColor(dT80,  mA.timeTo80,  mB.timeTo80,  true);
      deltaColor(dPeak, mA.maxSpeedReached, mB.maxSpeedReached, false);

      const stateEl = document.getElementById("co-tracker-state");
      if (stateEl) {
        const anyRunning = mA.state === "running" || mB.state === "running";
        const anyDone = mA.state === "done" || mB.state === "done";
        const bothDone = mA.state === "done" && mB.state === "done";
        stateEl.textContent = bothDone
          ? "MEASUREMENT COMPLETE — R to reset"
          : anyDone
            ? "ONE DONE — WAITING…"
            : anyRunning
              ? "MEASURING…"
              : "WAITING FOR THROTTLE";
        stateEl.className = "co-tracker-state " + (bothDone ? "co-tracker-done" : anyRunning ? "co-tracker-running" : "");
      }
    } else {
      const toHeading = (r: number): string => `${(((r * 180 / Math.PI) % 360 + 360) % 360).toFixed(0)}°`;
      set("co-a-head", toHeading(snapA.rotY));
      set("co-b-head", toHeading(snapB.rotY));
      set("co-a-pos", `${snapA.x.toFixed(0)},${snapA.z.toFixed(0)}`);
      set("co-b-pos", `${snapB.x.toFixed(0)},${snapB.z.toFixed(0)}`);
    }
  }

  private updateCamera(): void {
    if (this.vehicles.length < 2) return;
    const snapA = this.vehicles[0].controller.snapshot();
    const snapB = this.vehicles[1].controller.snapshot();
    const cx = (snapA.x + snapB.x) / 2;
    const cz = (snapA.z + snapB.z) / 2;

    if (this.arena === "skidpan") {
      // Overhead view workaround — the dynamic follow camera produces jitter
      // when cars separate quickly on a skidpan. A fixed top-down angle is
      // stable and readable. TODO: replace with a smooth zoom-to-fit follow
      // camera once the lerp/easing approach is validated.
      this.scene.camera.position.set(cx, 220, cz + 0.1);
      this.scene.camera.lookAt(cx, 0, cz);
      return;
    }

    const dx = snapB.x - snapA.x;
    const dz = snapB.z - snapA.z;
    const spread = Math.sqrt(dx * dx + dz * dz);
    const height = Math.max(50, spread * 0.9 + 20);
    const back = Math.max(50, spread * 0.6 + 15);
    this.scene.camera.position.set(cx, height, cz - back);
    this.scene.camera.lookAt(cx, 0, cz);
  }

  private buildArena(): void {
    const floorGeo = new THREE.PlaneGeometry(600, 600);
    const floor = new THREE.Mesh(floorGeo, new THREE.MeshBasicMaterial({ color: 0x04060d }));
    floor.rotation.x = -Math.PI / 2;
    this.scene.scene.add(floor);
    this.scene.scene.add(buildGrid(600, 600, 20, 0x0a1f2e));

    if (this.arena === "drag") this.buildDragMarkers();
    else if (this.arena === "skidpan") this.buildSkidpanCircle();
    else if (this.arena === "weapons") this.buildWeaponsMarkers();

    this.scene.scene.add(new THREE.HemisphereLight(0x88ccff, 0x110022, 0.6));
    const dir = new THREE.DirectionalLight(0xffffff, 0.4);
    dir.position.set(40, 80, 30);
    this.scene.scene.add(dir);
  }

  private buildDragMarkers(): void {
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute("position", new THREE.Float32BufferAttribute([-20, 0.03, -150, 20, 0.03, -150], 3));
    this.scene.scene.add(new THREE.LineSegments(lineGeo, new THREE.LineBasicMaterial({ color: 0x22d3ee })));

    const finGeo = new THREE.BufferGeometry();
    finGeo.setAttribute("position", new THREE.Float32BufferAttribute([-20, 0.03, 150, 20, 0.03, 150], 3));
    this.scene.scene.add(new THREE.LineSegments(finGeo, new THREE.LineBasicMaterial({ color: 0xa3e635 })));

    const divPts: number[] = [];
    for (let z = -150; z <= 150; z += 8) divPts.push(0, 0.03, z, 0, 0.03, z + 4);
    const divGeo = new THREE.BufferGeometry();
    divGeo.setAttribute("position", new THREE.Float32BufferAttribute(divPts, 3));
    this.scene.scene.add(new THREE.LineSegments(divGeo, new THREE.LineBasicMaterial({ color: 0xfb923c, opacity: 0.5, transparent: true })));
  }

  private buildSkidpanCircle(): void {
    const radius = 80;
    const segs = 128;
    const pts: number[] = [];
    for (let i = 0; i < segs; i++) {
      const a1 = (i / segs) * Math.PI * 2;
      const a2 = ((i + 1) / segs) * Math.PI * 2;
      pts.push(Math.cos(a1) * radius, 0.03, Math.sin(a1) * radius);
      pts.push(Math.cos(a2) * radius, 0.03, Math.sin(a2) * radius);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    this.scene.scene.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x22d3ee, opacity: 0.5, transparent: true })));
  }

  private buildWeaponsMarkers(): void {
    const midGeo = new THREE.BufferGeometry();
    midGeo.setAttribute("position", new THREE.Float32BufferAttribute([-20, 0.03, 0, 20, 0.03, 0], 3));
    this.scene.scene.add(new THREE.LineSegments(midGeo, new THREE.LineBasicMaterial({ color: 0xf472b6, opacity: 0.6, transparent: true })));
  }
}

function findPresetName(u: CarUpgrade): string {
  for (const [k, v] of Object.entries(CAR_PRESETS)) {
    if ((Object.keys(u) as Array<keyof CarUpgrade>).every((key) => u[key] === v[key])) return k;
  }
  return "custom";
}

function buildGrid(w: number, h: number, step: number, color: number): THREE.LineSegments {
  const pts: number[] = [];
  const hw = w / 2, hh = h / 2;
  for (let x = -hw; x <= hw; x += step) pts.push(x, 0.01, -hh, x, 0.01, hh);
  for (let z = -hh; z <= hh; z += step) pts.push(-hw, 0.01, z, hw, 0.01, z);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
  return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color }));
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}
